/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  Box,
  Grid,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@material-ui/core';
import { Link } from 'gatsby';
import PopupState, { bindPopover, bindTrigger } from 'material-ui-popup-state';
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { DATE_FORMAT, JobActionsType, JobStatusType } from '../..';
import {
  GetJobsInput,
  Job,
  JobLocation,
  JobStatus,
} from '../../../../../__generated__/pgeplus-types';
import {
  TableControlsActions,
  TableControlsType,
} from '../../../../../hooks/pge-plus/useJobsList';
import { PageTextContentType } from '../../../../../hooks/useContentMgmt';
import routes from '../../../../../routes';
import colors from '../../../../../themes/main-colors';
import { toDateString } from '../../../../../util/format';
import { useComponentSize } from '../../../../../util/style-utils';
import TextField from '../../../../text-field';
import { Filter } from '../controls/Filter';
import { Search } from '../controls/Search';
import { Sort } from '../controls/Sort';

type DesktopViewProps = {
  content: PageTextContentType;
  tableControls: TableControlsType;
  jobActions: JobActionsType;
  searchValue: string;
  setSearchValue: Dispatch<SetStateAction<string>>;
  jobs?: Job[];
  searchParams?: GetJobsInput;
};

export const DesktopView = (props: DesktopViewProps) => {
  const {
    content,
    tableControls,
    jobs,
    jobActions,
    searchValue,
    setSearchValue,
    searchParams,
  } = props;
  const theme = useTheme();
  const [filterByVal, setFilterByVal] = useState<string | undefined>('All');
  const filterByAnchorEl = useRef<HTMLInputElement>(null);
  const { width: filterByWidth } = useComponentSize(filterByAnchorEl);
  const [sortByVal, setSortByVal] = useState<string | undefined>(' ');
  const sortByAnchorEl = useRef<HTMLInputElement>(null);
  const { width: sortByWidth } = useComponentSize(sortByAnchorEl);

  const getJobAddress = (jobLocation: JobLocation | undefined) => {
    let fullAddress: string = '-';
    if (jobLocation?.addressLine1) {
      fullAddress = jobLocation.addressLine1;
      if (jobLocation.city) {
        fullAddress += ' ' + jobLocation.city;
      }
      if (jobLocation.postalCode) {
        fullAddress += ' ' + jobLocation.postalCode;
      }
    }
    return fullAddress;
  };

  useEffect(() => {
    if (content) {
      if (searchParams?.status) {
        setFilterByVal(getDisplayNameForStatus(searchParams?.status));
      }

      if (searchParams?.sort) {
        const sortByValue = Object.keys(searchParams?.sort)?.[0];
        setSortByVal(getDisplayNameForSorting(sortByValue));
      }
    }
  }, [searchParams, content]);

  const getDisplayNameForStatus = (jobStatus: JobStatus[]) => {
    const labels = {
      '': content.get('ALL'),
      [JobStatus.Created]: content.get('LEADS'),
      [[JobStatus.Accepted, JobStatus.InProgress].join()]: content.get(
        'OPEN_JOB',
      ),
      [[JobStatus.Completed, JobStatus.ServiceCompleted].join()]: content.get(
        'COMPLETED_JOBS',
      ),
      [JobStatus.Rejected]: content.get('DECLINED'),
      [JobStatus.Canceled]: content.get('CANCELLED_JOBS'),
      [JobStatus.Expired]: content.get('EXPIRED'),
    };

    return labels[jobStatus.join()];
  };

  const getDisplayNameForSorting = (sortBy: string = '') => {
    const labels = {
      createdAt: content.get('REUSABLE_DATE'),
      applicationNo: content.get('REFERENCE_NO'),
      customerName: content.get('CUSTOMER_NAME'),
    };

    return (
      labels[sortBy as 'createdAt' | 'applicationNo' | 'customerName'] || ''
    );
  };

  return (
    <Fragment>
      <Fragment>
        <Grid
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: theme.spacing(2),
          }}
        >
          <Grid
            css={{
              width: '100%',
              marginLeft: theme.spacing(2),
              marginRight: theme.spacing(2),
            }}
          >
            <Search
              content={content}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              search={val => tableControls(TableControlsActions.SEARCH, val)}
            />
          </Grid>
          <Grid css={{ width: '100%', marginLeft: theme.spacing(2) }}>
            <PopupState variant="popover" popupId="sort-by">
              {popupState => (
                <Box>
                  <TextField
                    name={'sortBy'}
                    value={sortByVal?.trim()}
                    label={content.get('SORT_BY')}
                    ref={sortByAnchorEl}
                    {...bindTrigger(popupState)}
                    size="small"
                  />
                  <Popover
                    {...bindPopover(popupState)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'center',
                    }}
                    PaperProps={{
                      style: { width: sortByWidth },
                    }}
                  >
                    <Sort
                      content={content}
                      sort={val =>
                        tableControls(TableControlsActions.SORT, val)
                      }
                      setSortByVal={setSortByVal}
                      value={searchParams?.sort || undefined}
                    />
                  </Popover>
                </Box>
              )}
            </PopupState>
          </Grid>
          <Grid
            css={{
              width: '100%',
              marginLeft: theme.spacing(2),
              cursor: 'pointer',
            }}
          >
            <PopupState variant="popover" popupId="filter-by">
              {popupState => (
                <Box>
                  <TextField
                    name={'filterBy'}
                    value={filterByVal}
                    label={content.get('FILTER_BY_STATUS')}
                    ref={filterByAnchorEl}
                    {...bindTrigger(popupState)}
                    size="small"
                  />
                  <Popover
                    {...bindPopover(popupState)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'center',
                    }}
                    PaperProps={{
                      style: { width: filterByWidth },
                    }}
                  >
                    <Box
                      css={{
                        padding: theme.spacing(1),
                      }}
                    >
                      <Filter
                        content={content}
                        filter={val =>
                          tableControls(TableControlsActions.FILTER, val)
                        }
                        setFilterByVal={setFilterByVal}
                        value={searchParams?.status || undefined}
                        setOpenDrawer={val =>
                          popupState.setOpen(val as boolean)
                        }
                      />
                    </Box>
                  </Popover>
                </Box>
              )}
            </PopupState>
          </Grid>
        </Grid>
      </Fragment>
      <Fragment>
        <TableContainer component={Box}>
          <Table
            size="medium"
            css={{
              borderRadius: 0,
              boxShadow: 'none',
            }}
          >
            <TableHead>
              <TableRow
                css={{
                  borderBottom: `1px solid ${colors.disableColor}`,
                  '& th': { borderBottom: 'none' },
                }}
              >
                <TableCell
                  css={{
                    fontWeight: 'bold',
                    fontSize: theme.spacing(2),
                  }}
                >
                  {content.get('REFERENCE_NO')}
                </TableCell>
                <TableCell
                  css={{
                    fontWeight: 'bold',
                    fontSize: theme.spacing(2),
                  }}
                  align="left"
                >
                  {content.get('REUSABLE_DATE')}
                </TableCell>
                <TableCell
                  css={{
                    fontWeight: 'bold',
                    fontSize: theme.spacing(2),
                  }}
                  align="left"
                >
                  {content.get('CUSTOMER_NAME')}
                </TableCell>
                <TableCell
                  css={{
                    fontWeight: 'bold',
                    fontSize: theme.spacing(2),
                  }}
                  align="left"
                >
                  {content.get('ADDRESS')}
                </TableCell>
                <TableCell
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    '&>span': {
                      fontWeight: 'bold',
                      fontSize: theme.spacing(2),
                    },
                  }}
                  align="left"
                >
                  <Typography component={'span'}>
                    {content.get('REUSABLE_STATUS')}
                  </Typography>
                  <Typography component={'span'}>
                    {content.get('ACTION')}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs?.map(job => (
                <TableRow
                  key={job.applicationNo}
                  css={{
                    borderRadius: 0,
                    boxShadow: 'none',
                    backgroundColor: job.isOpened
                      ? 'transparent'
                      : colors.riverBlue,
                    borderLeft: job.isOpened
                      ? 'none'
                      : `2px solid ${colors.noirBlur}`,
                  }}
                >
                  <TableCell
                    align="left"
                    css={{
                      verticalAlign: 'top',
                      fontSize: theme.spacing(2),
                      color: colors.tableBodyData,
                    }}
                  >
                    <Link
                      to={`${routes.PGE_PLUS_INSTALLER_PORTAL_JOB_DETAILS.replace(
                        ':jobId',
                        job.id,
                      )}`}
                      css={{
                        textDecoration: 'none',
                        color: colors.noirBlur,
                      }}
                    >
                      {job.applicationNo}
                    </Link>
                  </TableCell>
                  <TableCell
                    align="left"
                    css={{
                      verticalAlign: 'top',
                      fontSize: theme.spacing(2),
                      color: colors.tableBodyData,
                    }}
                  >
                    {toDateString(job.createdAt, DATE_FORMAT)}
                  </TableCell>
                  <TableCell
                    align="left"
                    css={{
                      verticalAlign: 'top',
                      fontSize: theme.spacing(2),
                      color: colors.tableBodyData,
                    }}
                  >
                    {`${job.jobLocation.firstName} ${job.jobLocation.lastName}`}
                  </TableCell>
                  <TableCell
                    align="left"
                    css={{
                      verticalAlign: 'top',
                      fontSize: theme.spacing(2),
                      color: colors.tableBodyData,
                    }}
                  >
                    {getJobAddress(job?.jobLocation)}
                  </TableCell>
                  <TableCell
                    align="left"
                    css={{
                      verticalAlign: 'top',
                      fontSize: theme.spacing(2),
                      color: colors.tableBodyData,
                    }}
                  >
                    {jobActions(job)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Fragment>
    </Fragment>
  );
};
